export type ThemePreference = "light" | "dark" | "system";

export const THEME_STORAGE_KEY = "bavimail-theme";
export const SYSTEM_THEME_QUERY = "(prefers-color-scheme: dark)";

/** A resolved mode. Each mode remembers its own dashboard palette (src/lib/palettes.ts). */
export type ThemeMode = "light" | "dark";

export const PALETTE_STORAGE_KEY: Record<ThemeMode, string> = {
  dark: "bavimail-palette-dark",
  light: "bavimail-palette-light",
};

// "classic-light" is the inverted light mode the site has always had, so
// nothing changes for anyone who never opens Appearance.
export const DEFAULT_PALETTE_ID: Record<ThemeMode, string> = {
  dark: "charcoal",
  light: "classic-light",
};

const PALETTE_ID_PATTERN = /^[a-z0-9-]{1,40}$/;

/** Ids only ever reach the DOM as a data attribute, but keep them boring anyway. */
export function parsePaletteId(value: string | null, mode: ThemeMode): string {
  return value !== null && PALETTE_ID_PATTERN.test(value) ? value : DEFAULT_PALETTE_ID[mode];
}

export function parseTheme(value: string | null): ThemePreference {
  return value === "light" || value === "dark" ? value : "system";
}

// Runs in the head before paint. No user-controlled values enter this script.
// The matching attribute is the only difference React must tolerate at hydration.
export const THEME_INIT_SCRIPT = `(() => {
  let preference = 'system';
  try {
    const saved = localStorage.getItem('${THEME_STORAGE_KEY}');
    if (saved === 'light' || saved === 'dark') preference = saved;
  } catch {}
  const dark = preference === 'dark' ||
    (preference === 'system' && matchMedia('${SYSTEM_THEME_QUERY}').matches);
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  const paletteId = ${PALETTE_ID_PATTERN};
  for (const [mode, attribute, fallback] of [
    ['dark', 'paletteDark', '${DEFAULT_PALETTE_ID.dark}'],
    ['light', 'paletteLight', '${DEFAULT_PALETTE_ID.light}'],
  ]) {
    let id = fallback;
    try {
      const saved = localStorage.getItem('bavimail-palette-' + mode);
      if (saved !== null && paletteId.test(saved)) id = saved;
    } catch {}
    document.documentElement.dataset[attribute] = id;
  }
})();`;

// Used only in browsers when storage is unavailable, never as SSR state.
let memoryPreference: ThemePreference | null = null;
const memoryPalette: Partial<Record<ThemeMode, string>> = {};
let themeTransition: ViewTransition | undefined;

/** What the user chose, as opposed to the mode it currently resolves to. */
export function getThemePreference(): ThemePreference {
  if (memoryPreference !== null) return memoryPreference;
  try {
    return parseTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return "system";
  }
}

function applyTheme(preference: ThemePreference) {
  const dark =
    preference === "dark" ||
    (preference === "system" && window.matchMedia(SYSTEM_THEME_QUERY).matches);
  document.documentElement.dataset.theme = dark ? "dark" : "light";
}

const PALETTE_ATTRIBUTE: Record<ThemeMode, "paletteDark" | "paletteLight"> = {
  dark: "paletteDark",
  light: "paletteLight",
};
const MODES: ThemeMode[] = ["dark", "light"];

function readPaletteId(mode: ThemeMode): string {
  const remembered = memoryPalette[mode];
  if (remembered) return remembered;
  try {
    return parsePaletteId(window.localStorage.getItem(PALETTE_STORAGE_KEY[mode]), mode);
  } catch {
    return DEFAULT_PALETTE_ID[mode];
  }
}

function applyPalettes() {
  for (const mode of MODES) {
    document.documentElement.dataset[PALETTE_ATTRIBUTE[mode]] = readPaletteId(mode);
  }
}

/** The palette id mirrored on <html> for a mode; the default before the init script runs. */
export function getPaletteId(mode: ThemeMode): string {
  return parsePaletteId(document.documentElement.dataset[PALETTE_ATTRIBUTE[mode]] ?? null, mode);
}

export function setPaletteId(mode: ThemeMode, id: string) {
  const next = parsePaletteId(id, mode);
  memoryPalette[mode] = next;
  try {
    window.localStorage.setItem(PALETTE_STORAGE_KEY[mode], next);
    delete memoryPalette[mode];
  } catch {
    // Still applies for this page session when persistence is blocked.
  }
  document.documentElement.dataset[PALETTE_ATTRIBUTE[mode]] = next;
}

/** Fires when the resolved mode or either palette id changes. */
export function subscribeToAppearance(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme", "data-palette-dark", "data-palette-light"],
  });
  return () => observer.disconnect();
}

export function setTheme(preference: ThemePreference) {
  memoryPreference = preference;
  try {
    if (preference === "system") window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    memoryPreference = null;
  } catch {
    // The toggle still works for this page session when persistence is blocked.
  }
  applyTheme(preference);
}

/** Resolves when the switch has finished animating; undefined when it was instant or ignored. */
export function toggleTheme(): Promise<void> | undefined {
  // Snapshot both finished palettes instead of interpolating the inversion
  // filter, which would turn the whole page grey halfway through the change.
  if (themeTransition) return undefined;
  const next = document.documentElement.dataset.theme === "light" ? "dark" : "light";
  if (
    typeof document.startViewTransition !== "function" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    setTheme(next);
    return undefined;
  }

  const root = document.documentElement;
  root.dataset.themeTransition = "";
  try {
    const transition = document.startViewTransition(() => setTheme(next));
    themeTransition = transition;
    // A hidden tab or interrupted snapshot can skip the animation. The update
    // still runs; handle the ready rejection without leaving an unhandled error.
    void transition.ready.catch(() => {});
    return transition.finished
      .catch(() => setTheme(next))
      .finally(() => {
        themeTransition = undefined;
        delete root.dataset.themeTransition;
      });
  } catch {
    delete root.dataset.themeTransition;
    setTheme(next);
    return undefined;
  }
}

/** Mount once in the root shell; returns cleanup for navigation/HMR/StrictMode. */
export function startThemeSync() {
  const system = window.matchMedia(SYSTEM_THEME_QUERY);
  const followSystem = () => applyTheme(getThemePreference());
  const followStorage = (event: StorageEvent) => {
    const paletteKeys = Object.values(PALETTE_STORAGE_KEY);
    const relevant =
      event.key === null || event.key === THEME_STORAGE_KEY || paletteKeys.includes(event.key);
    if (!relevant) return;
    // Ignore unrelated sessionStorage events without accessing a blocked store.
    try {
      if (event.storageArea && event.storageArea !== window.localStorage) return;
    } catch {
      return;
    }
    if (event.key !== null && paletteKeys.includes(event.key)) {
      applyPalettes();
      return;
    }
    memoryPreference = null;
    applyTheme(event.key === null ? getThemePreference() : parseTheme(event.newValue));
    if (event.key === null) applyPalettes();
  };

  followSystem();
  applyPalettes();
  system.addEventListener("change", followSystem);
  window.addEventListener("storage", followStorage);
  return () => {
    system.removeEventListener("change", followSystem);
    window.removeEventListener("storage", followStorage);
  };
}
